import * as React from 'react'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import { PlatformMessageDto } from '@halo/dtos/platform-message.dto'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { DatePicker, Input, Select, Switch } from 'antd'
import { AppService } from '@halo/services/app.service'
import { AppState } from '@halo/stores/app.state'
import { Container } from 'typescript-ioc'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { Drawer, executeCleanDrawerRender, Well } from '@pushly/aqe/lib/components'
import { Editor } from 'react-draft-wysiwyg'
import Draft, { EditorState, ContentState, convertToRaw, convertFromRaw, convertFromHTML } from 'draft-js'
import draftToHtml from 'draftjs-to-html/lib/draftjs-to-html'
import { classnames } from '@halo/_utils/classnames'
import './platform-messages.scss'
import * as moment from 'moment'
import { Moment } from 'moment'
import { DomainService } from '@halo/services/domain.service'
import randomstring from 'randomstring'
import titleCase from 'title-case'
import deepEqual from 'react-fast-compare'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { TAGS } from '@halo/enums/message-tags'
import { legacyValidateFields } from '@halo/_utils/antd'

interface IPlatformMessageEditor extends FormComponentProps {
    allMessages: PlatformMessageDto[]
    message?: any
    visible: boolean
    onClose?: (e: any) => void
    onMessageUpdated?: () => void
}

interface IPMEState {
    editorState: any
    domainRestricted: boolean
    useMinVersionReq: boolean
    useMaintenanceMode: boolean
    domains: any[]
    versions: any[]
}

export const PlatformMessageEditor = Form.create<IPlatformMessageEditor>()(
    class extends React.Component<IPlatformMessageEditor, IPMEState> {
        public state: IPMEState = {
            editorState: this.props.message?.message || EditorState.createEmpty(),
            domainRestricted: this.props.message?.domain_ids?.length > 0 || false,
            useMinVersionReq: !!this.props.message?.min_version_req,
            useMaintenanceMode: false,
            domains: [],
            versions: [],
        }

        protected appState: AppState
        protected appService: AppService
        protected domainService: DomainService

        public constructor(props: IPlatformMessageEditor) {
            super(props)

            this.appState = Container.get(AppState)
            this.appService = Container.get(AppService)
            this.domainService = Container.get(DomainService)

            executeCleanDrawerRender(this)
        }

        public async componentDidMount() {
            await this.fetchDependencies()
        }

        public render() {
            const message = this.props.message?.message
            const minVersion = this.props.message?.min_version_req
            const isEditMode = this.props.message?.id !== undefined

            return (
                <Drawer
                    className={classnames('platform-message-editor')}
                    getContainer={this.appService.getAppContainer}
                    title={isEditMode ? 'Edit Message' : 'New Message'}
                    placement="right"
                    closable={true}
                    onClose={this.props.onClose}
                    visible={this.props.visible}
                    onSubmit={this.handleSubmit}
                >
                    <Form {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT} className="hide-inline-errors">
                        <Well className="nested" title="Message Details" mode="ghost" hideFooter={true}>
                            <Form.Item label="Title" className="title">
                                {this.props.form.getFieldDecorator('messageTitle', {
                                    initialValue: this.props.message?.title,
                                    rules: [
                                        {
                                            required: true,
                                            message: 'A title is required.',
                                        },
                                    ],
                                })(<Input size="small" />)}
                            </Form.Item>

                            <Form.Item label="Message">
                                {this.props.form.getFieldDecorator('message', {
                                    initialValue: this.state.editorState,
                                    rules: [
                                        {
                                            required: message ? false : true,
                                            // message: 'A message is required.',
                                            validator: this.validateMessage,
                                        },
                                    ],
                                })(
                                    <Editor
                                        editorState={this.state.editorState}
                                        toolbar={{
                                            options: ['inline', 'link'],
                                            inline: {
                                                inDropdown: false,
                                                options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
                                            },
                                        }}
                                        defaultEditorState={this.state.editorState}
                                        toolbarClassName="pmsg-toolbar"
                                        wrapperClassName="pmsg-wrapper"
                                        editorClassName="pmsg-editor"
                                        onEditorStateChange={this.handleEditorStateChange}
                                    />,
                                )}
                            </Form.Item>

                            <div className="delivery-window">
                                <div className="delivery-start">
                                    <Form.Item label="Start Time">
                                        {this.props.form.getFieldDecorator('startTime', {
                                            initialValue: this.props.message
                                                ? moment(this.props.message?.start_time)
                                                : null,
                                            rules: [
                                                {
                                                    required: true,
                                                    message: 'Start Date is required.',
                                                },
                                            ],
                                        })(
                                            <DatePicker
                                                showTime={{
                                                    format: 'HH:mm',
                                                }}
                                                format={'ll LT'}
                                            />,
                                        )}
                                    </Form.Item>
                                </div>
                                <div className="delivery-end">
                                    <Form.Item label="End Time">
                                        {this.props.form.getFieldDecorator('endTime', {
                                            initialValue: this.props.message?.end_time
                                                ? moment(this.props.message?.end_time)
                                                : null,
                                            rules: [
                                                {
                                                    validator: this.validateEndDate,
                                                    // message: 'End Date is required in maintenance mode.'
                                                },
                                            ],
                                        })(
                                            <DatePicker
                                                showTime={{
                                                    format: 'HH:mm',
                                                }}
                                                format={'ll LT'}
                                            />,
                                        )}
                                    </Form.Item>

                                    <Form.Item label="Restrict to Domains">
                                        <div className="switch-row">
                                            <div className="switch-left">
                                                <Switch
                                                    size="small"
                                                    checked={this.state.domainRestricted}
                                                    disabled={isEditMode}
                                                    onChange={(domainRestricted) =>
                                                        this.setState(({ useMaintenanceMode, useMinVersionReq }) => {
                                                            if (domainRestricted) {
                                                                useMaintenanceMode = false
                                                                useMinVersionReq = false
                                                            }

                                                            return {
                                                                useMinVersionReq,
                                                                useMaintenanceMode,
                                                                domainRestricted,
                                                            }
                                                        })
                                                    }
                                                />
                                            </div>
                                            <div className="switch-right">
                                                {this.state.domainRestricted &&
                                                    this.props.form.getFieldDecorator('domainIds', {
                                                        initialValue: this.props.message?.domain_ids,
                                                    })(
                                                        <Select size="small" mode="multiple" className="domains">
                                                            {this.state.domains?.map((domain) => {
                                                                return (
                                                                    <Select.Option key={domain.id} value={domain.id}>
                                                                        {domain.display_name}
                                                                    </Select.Option>
                                                                )
                                                            })}
                                                        </Select>,
                                                    )}
                                            </div>
                                        </div>
                                    </Form.Item>

                                    <Form.Item label="Minimum Version Requirement">
                                        <div className="switch-row">
                                            <div className="switch-left">
                                                <Switch
                                                    size="small"
                                                    checked={this.state.useMinVersionReq}
                                                    disabled={isEditMode}
                                                    onChange={(useMinVersionReq) =>
                                                        this.setState(({ useMaintenanceMode, domainRestricted }) => {
                                                            if (useMinVersionReq) {
                                                                useMaintenanceMode = false
                                                                domainRestricted = false
                                                            }

                                                            return {
                                                                useMinVersionReq,
                                                                useMaintenanceMode,
                                                                domainRestricted,
                                                            }
                                                        })
                                                    }
                                                />
                                            </div>
                                            <div className="switch-right">
                                                {this.state.useMinVersionReq &&
                                                    this.props.form.getFieldDecorator('minVersionReq', {
                                                        initialValue: minVersion,
                                                    })(
                                                        <Select size="small" disabled={isEditMode}>
                                                            {this.state.versions.reverse().map((version) => {
                                                                return (
                                                                    <Select.Option key={version} value={version}>
                                                                        {version}
                                                                    </Select.Option>
                                                                )
                                                            })}
                                                        </Select>,
                                                    )}
                                            </div>
                                        </div>
                                    </Form.Item>
                                </div>
                            </div>

                            <Form.Item label="Maintenance Mode">
                                <div className="switch-row">
                                    <div className="switch-left">
                                        <Switch
                                            size="small"
                                            checked={this.state.useMaintenanceMode}
                                            disabled={isEditMode}
                                            onChange={(useMaintenanceMode) =>
                                                this.setState(({ useMinVersionReq, domainRestricted }) => {
                                                    if (useMaintenanceMode) {
                                                        useMinVersionReq = false
                                                        domainRestricted = false
                                                    }

                                                    return {
                                                        useMaintenanceMode,
                                                        useMinVersionReq,
                                                        domainRestricted,
                                                    }
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                            </Form.Item>

                            <Form.Item label="Tags">
                                {this.props.form.getFieldDecorator('tags', {
                                    initialValue: this.props.message?.tags ? this.props.message.tags : [],
                                })(
                                    <Select mode="multiple" size="small" disabled={isEditMode}>
                                        <Select.Option key={TAGS.AUTOMATED} value={TAGS.AUTOMATED}>
                                            {titleCase(TAGS.AUTOMATED)}
                                        </Select.Option>
                                        <Select.Option key={TAGS.DEMO_DOMAINS} value={TAGS.DEMO_DOMAINS}>
                                            {titleCase(TAGS.DEMO_DOMAINS)}
                                        </Select.Option>
                                    </Select>,
                                )}
                            </Form.Item>
                        </Well>
                    </Form>
                </Drawer>
            )
        }

        protected async fetchDependencies(): Promise<void> {
            const domainFields = ['id', 'display_name']

            const { ok, data: domains } = await this.domainService.fetchAll({
                showLoadingScreen: false,
                query: {
                    pagination: false,
                    fields: domainFields.join(','),
                },
            })

            const { versions } = await this.appService.getApplicationVersions()

            if (ok) {
                this.setState({ domains, versions })
            }
        }

        protected validateEndDate = (rule: any, value: Moment, callback: Function): void => {
            if (!this.state.useMaintenanceMode || !!value) {
                if (value) {
                    const error = value <= this.props.form.getFieldValue('startTime')
                    if (error) {
                        callback('End Date must be a date/time which occurs after the specified Start Time.')
                    }
                }
                callback()
            } else {
                callback('End Date is required in maintenance mode.')
            }
        }

        protected validateMessage = (rule: any, value: string, callback: Function): void => {
            const html = draftToHtml(value)
            const emptyValueRegEx = /^[\r\n\s]*\<p[\s]*\>\<\/p\>[\r\n\s]*$/i

            const isEmptyValue = emptyValueRegEx.test(html)
            if (isEmptyValue) {
                if (this.props?.message) {
                    callback('A message cannot be updated as empty.')
                } else {
                    callback('A message is required.')
                }
            }
            callback()
        }

        protected handleEditorStateChange = (editorState: any): void => {
            return this.setState({ editorState })
        }

        protected getCurrentValues() {
            let values: any = {}

            // title
            const messageTitle = this.props.form.getFieldValue('messageTitle')
            if (!!messageTitle) {
                values.title = messageTitle
                if (deepEqual(values.title, this.props.message?.title)) {
                    delete values.title
                }
            }

            // message
            const rawMessage = convertToRaw(this.state.editorState.getCurrentContent())
            if (!!rawMessage) {
                const html = draftToHtml(rawMessage)

                // default empty value will always contain "<p></p>↵"
                const emptyValueRegEx = /^[\r\n\s]*\<p[\s]*\>\<\/p\>[\r\n\s]*$/i

                const isEmptyValue = emptyValueRegEx.test(html)
                if (!isEmptyValue) {
                    values.message = html
                    if (deepEqual(values.message, this.props.message?.message)) {
                        delete values.message
                    }
                }
            }

            // minVersionRequired?
            const minVersionRequired = this.props.form.getFieldValue('minVersionReq')
            if (this.state.useMinVersionReq) {
                values.minVersionReq = minVersionRequired
                if (deepEqual(values.minVersionReq, this.props.message?.min_version_req)) {
                    delete values.minVersionReq
                }
            }

            // IsMaintenanceMode?
            if (!!values.title || !!values.messageTitle) {
                if (this.state.useMaintenanceMode) {
                    values.maintenanceMode = this.state.useMaintenanceMode
                    if (deepEqual(values.maintenanceMode, this.props.message?.maintenanceMode)) {
                        delete values.maintenanceMode
                    }
                }
            }

            // Timeframe
            const startTime = this.props.form.getFieldValue('startTime')
            const endTime = this.props.form.getFieldValue('endTime')
            if (!!startTime) {
                values.startTime = startTime.toISOString()
                if (deepEqual(values.startTime, this.props.message?.start_time)) {
                    delete values.startTime
                }
            }
            if (!!endTime) {
                values.endTime = endTime.toISOString()
                if (deepEqual(values.endTime, this.props.message?.end_time)) {
                    delete values.endTime
                }
            }

            // Domain IDs
            const domainIds = this.props.form.getFieldValue('domainIds')
            if (this.state.domainRestricted) {
                values.domainIds = domainIds
                if (deepEqual(values.domainIds, this.props.message?.domain_ids)) {
                    delete values.domainIds
                }
            }

            // Tags
            const tags = this.props.form.getFieldValue('tags')
            if (tags?.length > 0) {
                values.tags = tags
                if (deepEqual(values.tags, this.props.message?.tags)) {
                    delete values.tags
                }
            }

            return values
        }

        protected handleSubmit = async (): Promise<any> => {
            try {
                await legacyValidateFields(this.props.form)
                const mergedValues = this.getCurrentValues()

                if (this.props.message?.id) {
                    const updated = await this.appService.updateMessage(mergedValues, this.props.message?.id)
                    if (updated && this.props.onMessageUpdated) {
                        this.props.onMessageUpdated()
                    }
                } else {
                    const created = await this.appService.createMessage(mergedValues)
                    if (created && this.props.onMessageUpdated) {
                        this.props.onMessageUpdated()
                    }
                }
            } catch (err) {
                if (err) {
                    simpleFormErrorNotification(err)
                }
            }
        }
    },
)
